import { atTime, DOCK_EXAM_END, DOCK_EXAM_START, ES_LOWER_PREP_MINUTES, getPetIvLeadMinutes, getPetIvSlots, GYNECOLOGY_START, isPetScan, MAX_WAIT_MINUTES, PET_HEAD_IV_LEAD_MINUTES, RECEPTION_PREP_MINUTES } from "./rules"

/**
 * イベントの開始・終了をセットするobjectを返す
 * @param time 開始時刻
 * @param modalityTime モダリティの所要期間
 */
export const setStartAndEnd = (time: Date, modalityTime: string) => {
  const start = new Date(time)
  const end = new Date(time)
  end.setMinutes(time.getMinutes() + Number(modalityTime))
  return {"start": start, "end": end}
}

/**
 * 別の予約と重複しないよう、最適な予約イベントの時刻を返す
 * 全ての引数は、同一のモダリティを対象とする
 * @param eventSchedule 別の予約を考慮しない時の予約スケジュール
 * @param modalityEvent モダリティの既存予約イベント
 * @param modalityTime モダリティの所要時間
 */
export const setOptimizeSchedule = (eventSchedule: KV, modalityEvent: KV, modalityTime: string) => {
  let overlap: Boolean = false

  // Eventが重複した場合、重複しないように、後続に配置
  for (const i in modalityEvent) {
    const start = modalityEvent[i]["start"]
    const end = modalityEvent[i]["end"]
    // 予定の予約枠がeventと重複していないかチェック
    if ((start <= eventSchedule.start && eventSchedule.start <= end) ||
        (start <= eventSchedule.end && eventSchedule.end <= end) ||
        (eventSchedule.start <= start && end <= eventSchedule.end)
    ) {
      overlap = true
    }
  }
  // 重複していた場合、最適な位置を探す
  if (overlap) {
    let prevEnd = new Date()
    for (const i in modalityEvent) {
      const start = modalityEvent[i]["start"]
      const end = modalityEvent[i]["end"]

      // eventのendが、dockModalityStartに達していないと、スキップ
      if (end <= eventSchedule.start) {
        prevEnd = end
        continue
      }
      if (Number(i) === 0) {
        // Eventが最後の場合
        if (Number(i) === (modalityEvent.length - 1)) {
          eventSchedule = setStartAndEnd(end, modalityTime)
          break
        }
        prevEnd = end
        continue
      }

      // prevEndとeventSchedule.startの大きい方で比較
      let diff = (start.getTime() - prevEnd.getTime()) / (60*1000)  // minute
      if (prevEnd <= eventSchedule.start) {
        diff = (start.getTime() - eventSchedule.start.getTime()) / (60*1000)
      }
      // 差分が検査所要時間以上あれば、prevEndから予約を確保
      if (Number(diff) >= Number(modalityTime)) {
        if (prevEnd > eventSchedule.start) {
          eventSchedule = setStartAndEnd(prevEnd, modalityTime)
        }
        break
      }
      // Eventが最後の場合
      if (Number(i) === (modalityEvent.length - 1)) {
        if (end > eventSchedule.start) {
          eventSchedule = setStartAndEnd(end, modalityTime)
        }
      }
      prevEnd = end
    }
  }
  return eventSchedule
}

/**
 * 対象モダリティの予約情報を、startでsortしたobjectを返す
 * @param modality 対象モダリティ
 * @param events 予約イベント全体
 */
export const getModalityEvents = (modality: string, events: KV) => {
  const modalityEvents: KV = {}
  const modalityDuration: any = []
  for (const key in events) {
    const event = events[Number(key)]
    if (modality === event["resourceId"]) {
      const duration = {"start" : event["start"], "end" : event["end"]}
      modalityDuration.push(duration)
      modalityEvents[modality] = modalityDuration
    }
  }
  // モダリティの時間ごとにソート
  if (Object.keys(modalityEvents).length) {
    const eventArray: any[] = []
    const eventHash = Object.entries(modalityEvents[modality])
    eventHash.sort((p1: any, p2: any) => {
      return p1[1].start - p2[1].start
    })
    for (const i in eventHash) {
      eventArray.push(eventHash[i][1])
    }
    modalityEvents[modality] = eventArray
  }
  return modalityEvents
}

/**
 * ドック予約のイベントが重複していないかチェック
 * @param dockEvents そのドックで予約したイベント
 */
export const duplicateCheck = (dockEvents: KV) => {

  let prevEnd = new Date()
  // 要素のstartでソート
  let sortStarts = Object.keys(dockEvents).map(function(key) {
    return dockEvents[key]
  }).sort(function(a, b) {
    return (a.start < b.start) ? -1 : 1
  })

  for (const i in sortStarts) {
    // スケジュールなしのモダリティを除く
    if (sortStarts[i]["no_schedule"]) {
      continue
    }
    if (i === "0") {
      prevEnd = sortStarts[i]["end"]
      continue
    }
    if (sortStarts[i]["start"] < prevEnd) {
      return true
    }
    prevEnd = sortStarts[i]["end"]
  }

  return false
}
const modifyEmptyIfNull = (data: any) => (data) ? data : ""

/**
 * モダリティ、部位、造影剤から所要時間(分)を取得
 * @param baseResources modality_dock.json
 */
export const getDockModalityTime = (baseResources: KV[], modality: string, body1: string, body2: string, contrast: string) => {
  if (modality === 'PET-CT') modality = 'PET-CT_1'
  const modalityKey = baseResources.find(v => String(v['id']) === modality) || {}

  if (contrast === "") contrast = "なし"
  if (body1 === "" || body1 === null || body1 === '-') body1 = "なし"
  if (body2 === "" || body2 === null || body2 === '-') body2 = "なし"
  return modalityKey["body"][body1][body2][contrast]
}

/**
 * ドックコースの検査を優先順位順に並べる（優先順位にないモダリティは除外）
 */
export const sortDockContents = (contents: KV[], priority: string[]) => {
  const sorted: KV[] = []
  for (const i in priority) {
    for (const j in contents) {
      if (contents[j]["modality"] === priority[i]) {
        sorted.push(contents[j])
      }
    }
  }
  return sorted
}

type DockScheduleParams = {
  contents: KV[], reception: Date, events: KV, getModalityTime: (modality: string, body1: string, body2: string, contrast: string) => string, nonScheduleModality: string[]
  dockID?: string | number  // 自分のドック予約番号（更新時に、自分の PET の IV を他の患者の IV として数えないため）
  ignoreWait?: boolean  // 待ち時間の上限 MAX_WAIT_MINUTES を見ない（再計算ボタン。waitOver を返さず、並びを比べるときも後回しにしない）
}

const minutesOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes()
const getMinutes = (hhmm?: string) => {
  const [hours, minutes] = (hhmm ?? '').split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * その日の他の患者の PET-CT の IV 時刻（分）
 */
const getOtherIvMinutes = (events: KV, reception: Date, dockID?: string | number) => {
  const day = reception.toDateString()
  return (Object.values(events ?? {}) as KV[])
    .filter(v => {
      const props = v.extendedProps ?? {}
      if (!props.ivCheckin || new Date(v.start).toDateString() !== day) return false
      if (dockID !== undefined && String(props.dockID) === String(dockID)) return false
      return isPetScan(props.modalityID ?? v.resourceId ?? '', props.body1 ?? '', props.body2 ?? '')
    })
    .map(v => getMinutes(v.extendedProps.ivCheckin))
    .filter(v => !isNaN(v))
}

// IV を行う PET-CT の撮影（lead: IV から撮影開始まで(分)、minutes: 所要時間(分)）
type PetScan = { lead: number, minutes: number }

/**
 * PET-CT の IV の時刻（getPetIvSlots）を決める
 * earliest（前の検査の終了。最初の検査なら受付後の準備の後）以降の IV の時刻のうち、すべての撮影（頭部は IV の40分後、全身は70分後）の時刻に
 * PET-CT の予約枠が空いていて、他の患者の IV と10分以上離れている最初のもの
 * @param scans このコースの PET-CT の撮影（撮影する順）
 * @returns 置ける時刻が無ければ undefined
 */
const findIvSlot = (earliest: Date, reception: Date, scans: PetScan[], modalityEvents: KV[] | undefined, otherIvs: number[]) => {
  for (const time of getPetIvSlots(reception)) {
    const iv = atTime(reception, time)
    if (iv < earliest) continue
    const busy = scans.some(({ lead, minutes }) => {
      const schedule = setStartAndEnd(new Date(iv.getTime() + lead * 60000), String(minutes))
      return (modalityEvents ?? []).some(v => v.start < schedule.end && schedule.start < v.end)
    })
    if (busy) continue
    if (otherIvs.some(v => Math.abs(v - minutesOfDay(iv)) < 10)) continue
    return iv
  }
  return undefined
}

// コース内容の1件を [モダリティ, 部位1, 部位2, 造影] にする（PET-CT は1号機）
const modalityKeyOf = (key: KV): [string, string, string, string] => [
  key["modality"] === 'PET-CT' ? 'PET-CT_1' : key["modality"],
  modifyEmptyIfNull(key["modality_body1"]),
  modifyEmptyIfNull(key["modality_body2"]),
  modifyEmptyIfNull(key["modality_contrast"]),
]

// 待ち時間が上限 MAX_WAIT_MINUTES を超えた検査（exam: 検査の名前、minutes: その前の待ち時間）
export type WaitOver = { exam: string, minutes: number }

// 検査を途中まで並べた状態
type ChainState = {
  duration: KV
  overtime: boolean  // 終了時刻を過ぎた・IV の時刻が無い・待ち時間が上限を超えた（並びを比べるときに後回しにする）
  late: boolean  // 終了時刻 DOCK_EXAM_END を過ぎた
  wait: boolean  // まだ PET-CT を置いていない（IV から撮影までの待機が残っている）
  esPrep: boolean  // ES下部があり、まだES前の前処置（下剤）の時間を入れていない
  iv?: number  // PET-CT の IV の時刻（決めた後）
  ivUnavailable: boolean  // IV の時刻に合わせて PET-CT を置けなかった
  waitOver?: WaitOver  // 待ち時間が上限を超えた最初の検査
  prevEnd?: number  // 直前の検査の終了時刻（終了時刻の超過で仮置きする前の時刻）。最初の検査の前は undefined
}

const isOvertimeFirst = (a: ChainState, b: ChainState) => Number(a.overtime) - Number(b.overtime)

// PET-CT（IV）より前に行う検査
const BEFORE_PET = ['MMG', 'ABUS', 'US', 'US(L)']
// 順番の決まり: [後に行う検査, その前に終えておく検査]
const MUST_FOLLOW: [string, string[]][] = [
  ['ABUS', ['MMG']],
  ['ES', ['US', 'US(L)']],
]

/**
 * 再計算ボタンの自動スケジュール
 * 時間を管理する検査（nonScheduleModality 以外）だけを、受付時刻の20分後（8:30より前なら8:30）から順に並べ、
 * 各モダリティの既存予約と重複しない時刻に配置する
 *
 * 並べ方の条件
 * - MMG・ABUS・US・US(L) は PET-CT より前（IV より前）
 * - MMG は ABUS より前、US・US(L) は ES より前
 * - MRI・ES はどこでもよい（PET-CT の後でもよい）
 * - PET-CT は1号機に続けて配置する。IV は前の検査の終了後（先頭の場合は受付後の準備の後）
 * - PET-CT の撮影（CT だけの撮影を除く）は頭部 → 全身 → CT の順。IV は1回で、頭部は IV の40分後、全身は70分後に撮影する
 *   （両方あれば頭部の終了から全身まで20分）。IV の時刻は getPetIvSlots から選び、前の検査の終了より前の IV・他の患者と10分未満の IV は使わない。
 *   置ける IV の時刻が無ければ ivUnavailable（終了時刻の超過と同じく仮置き）
 * - 検査と検査の間の待ち時間は1回 MAX_WAIT_MINUTES 分まで（受付後の準備20分、ES下部・婦人科の決まった待機の分は数えない。
 *   PET-CT は前の検査の終了から IV までで数え、IV の後の待機・2つ目以降の撮影の前は数えない）。超える場合は waitOver
 * - ES下部（大腸内視鏡）がある場合は、ES（上部・下部）を続けて行い、その前に下剤の前処置として2時間あける（先頭の場合は受付から2時間後以降）
 * - US(L)（婦人科）は 9:15 より前には置かない（曜日・12:30 までに終わるかは checkGynecology で確認）
 * - 同じモダリティの検査どうしは、コース内容の順番のまま
 * 条件を満たす並びのうち、全検査が最も早く終わるものを選ぶ（終了時刻 DOCK_EXAM_END を超えない並びを優先、同じなら優先順位どおり）
 * @param contents sortDockContentsで並べたコース内容
 * @param reception 受付日時
 * @param events カレンダーの予約イベント
 * @param getModalityTime 所要時間の取得
 * @param nonScheduleModality スケジュール管理しないモダリティ（並べる対象から外す）
 * @returns duration: modality+body1+body2+contrast をキーとした予約時刻（時間を管理する検査のみ）, overtime: 終了時刻 DOCK_EXAM_END を過ぎたかどうか,
 *   ivUnavailable: PET-CT を置ける IV の時刻が無いかどうか, waitOver: 待ち時間が上限を超えた最初の検査
 */
export const calcDockSchedule = (params: DockScheduleParams) => {
  // モダリティごとの既存予約は、並びを何通り試しても同じなので一度だけ求める
  const cache: KV = {}
  const modalityEventsOf = (modality: string) => cache[modality] ||= getModalityEvents(modality, params.events)
  const otherIvs = getOtherIvMinutes(params.events, params.reception, params.dockID)

  const modalityOf = (v: KV): string => v["modality"]
  const isPet = (v: KV) => modalityOf(v) === 'PET-CT'
  const items = orderPetContents(params.contents.filter(v => !params.nonScheduleModality.includes(modalityOf(v))))
  const petScans: PetScan[] = items.filter(isPet).map(modalityKeyOf).filter(([m, b1, b2]) => isPetScan(m, b1, b2))
    .map(([m, b1, b2, c]) => ({ lead: getPetIvLeadMinutes(b1, b2), minutes: Number(params.getModalityTime(m, b1, b2, c)) }))
  const step = (state: ChainState, key: KV) => placeNext(state, key, params, { modalityEventsOf, otherIvs, petScans })
  const isEs = (v: KV) => modalityOf(v) === 'ES'
  const hasEsLower = items.some(v => isEs(v) && modalityKeyOf(v)[2] === '下部')
  const initial: ChainState = { duration: {}, overtime: false, late: false, wait: true, esPrep: hasEsLower, ivUnavailable: false }
  const result = ({ duration, late, ivUnavailable, waitOver }: ChainState) => ({ duration, overtime: late, ivUnavailable, waitOver })
  const baseline = items.reduce(step, initial)
  if (items.length < 2) return result(baseline)

  const end = (state: ChainState) => state.prevEnd ?? params.reception.getTime()
  const minutesOf = (key: KV) => Number(params.getModalityTime(...modalityKeyOf(key))) * 60000
  const closing = atTime(params.reception, DOCK_EXAM_END).getTime()

  // 次に置ける検査か（placed: 置いた検査の番号）
  const canPlace = (k: number, placed: boolean[]) => {
    const item = items[k]
    const modality = modalityOf(item)
    const unplaced = (pred: (v: KV) => boolean) => items.some((v, n) => !placed[n] && pred(v))
    const petStarted = items.some((v, n) => placed[n] && isPet(v))
    // PET-CT は続けて撮影する
    if (petStarted && unplaced(isPet) && !isPet(item)) return false
    // ES下部がある場合、ES（上部・下部）は前処置のあとに続けて行う
    if (hasEsLower && items.some((v, n) => placed[n] && isEs(v)) && unplaced(isEs) && !isEs(item)) return false
    // 同じモダリティどうしはコース内容の順番のまま
    if (items.some((v, n) => n < k && !placed[n] && modalityOf(v) === modality)) return false
    if (isPet(item)) {
      // IV より前に行う検査を終えてから
      if (unplaced(v => BEFORE_PET.includes(modalityOf(v)))) return false
    } else if (BEFORE_PET.includes(modality) && petStarted) {
      return false
    }
    const before = MUST_FOLLOW.find(([after]) => after === modality)?.[1] ?? []
    return !unplaced(v => before.includes(modalityOf(v)))
  }

  // 優先順位どおりの並びが条件を満たしていれば、それを基準に、より早く終わる並びだけを採用する
  const baselineValid = items.every((_, k) => canPlace(k, items.map((__, n) => n < k)))
  let best: ChainState = baselineValid ? baseline : { ...initial, overtime: true, prevEnd: Infinity }

  // 条件を満たす並びを、優先順位の順に試す
  // 途中まで並べた時点で、残りの検査を隙間なく並べても基準より早く終わらない並びは、その先を計算しない
  // （検査は直前の検査の終了後にしか置けないため、終了時刻はこの見込みより早くならない。結果は全通り試した場合と同じ）
  const placed = items.map(() => false)
  const search = (state: ChainState, count: number) => {
    if (count === items.length) {
      if (isOvertimeFirst(state, best) < 0 || (state.overtime === best.overtime && end(state) < end(best))) best = state
      return
    }
    const left = items.reduce((sum, v, n) => sum + (placed[n] ? 0 : minutesOf(v)), 0)
    const earliestEnd = end(state) + left + (petScans.length && state.wait ? petScans[0].lead * 60000 : 0) + (state.esPrep ? ES_LOWER_PREP_MINUTES * 60000 : 0)
    const overtime = Number(state.overtime || earliestEnd > closing) - Number(best.overtime)
    if (overtime > 0 || (overtime === 0 && earliestEnd >= end(best))) return
    for (let k = 0; k < items.length; k++) {
      if (placed[k] || !canPlace(k, placed)) continue
      placed[k] = true
      search(step(state, items[k]), count + 1)
      placed[k] = false
    }
  }
  search(initial, 0)
  // 条件を満たす並びが無い場合は、優先順位どおりの並び
  if (best.prevEnd === Infinity) best = baseline
  return result(best)
}

/**
 * PET-CT の撮影を、頭部 → 全身 → CT の順に並べ替える（ほかの検査の位置はそのまま）
 */
const orderPetContents = (items: KV[]) => {
  const rank = (v: KV) => {
    const [m, b1, b2] = modalityKeyOf(v)
    if (!isPetScan(m, b1, b2)) return 2
    return getPetIvLeadMinutes(b1, b2) === PET_HEAD_IV_LEAD_MINUTES ? 0 : 1
  }
  const pets = items.filter(v => v["modality"] === 'PET-CT').sort((a, b) => rank(a) - rank(b))
  let n = 0
  return items.map(v => (v["modality"] === 'PET-CT' ? pets[n++] : v))
}

type PlaceContext = { modalityEventsOf: (modality: string) => KV, otherIvs: number[], petScans: PetScan[] }

/**
 * 次の検査を、直前の検査の終了後（最初の検査は受付20分後・8:30以降）の空いている時刻に配置する
 */
const placeNext = (state: ChainState, key: KV, { reception, getModalityTime, nonScheduleModality, ignoreWait }: DockScheduleParams, { modalityEventsOf, otherIvs, petScans }: PlaceContext): ChainState => {
  const [modality, body1, body2, contrast] = modalityKeyOf(key)
  let eventSchedule: any
  let wait = state.wait
  let esPrep = state.esPrep
  let iv = state.iv
  let ivUnavailable = state.ivUnavailable
  let waitOver = state.waitOver
  const checkWait = !ignoreWait
  // PET-CT の撮影は IV の時刻に合わせる。それ以外は空いている時刻に置く
  // 待ち時間は、決まった待機を入れた時刻（earliest）から実際に置いた時刻までで数える
  // 最初の PET-CT の撮影は、前の検査の終了（最初の検査なら受付後の準備の後: origin）から IV までで数える
  const place = (earliest: Date, origin: Date) => {
    // 婦人科の US(L) は 9:15 から
    if (modality === 'US(L)') earliest = new Date(Math.max(earliest.getTime(), atTime(reception, GYNECOLOGY_START).getTime()))
    let schedule: any
    // PET-CT の2つ目以降の撮影・CT の前は待ち時間に数えない
    const afterIv = modality.startsWith('PET-CT') && (iv !== undefined || ivUnavailable)
    if (isPetScan(modality, body1, body2) && iv === undefined && !ivUnavailable) {
      iv = findIvSlot(earliest, reception, petScans, modalityEvents[modality], otherIvs)?.getTime()
      if (iv === undefined) {
        ivUnavailable = true
        // IV の時刻が無くても、撮影は前の検査の終了から IV の待機の分は後にする（並びを比べるときの見込みと合わせるため）
        earliest = new Date(earliest.getTime() + getPetIvLeadMinutes(body1, body2) * 60000)
      } else if (checkWait && !waitOver && iv - origin.getTime() > MAX_WAIT_MINUTES * 60000) waitOver = { exam: 'PET-CT の IV', minutes: (iv - origin.getTime()) / 60000 }
    }
    if (isPetScan(modality, body1, body2) && iv !== undefined) {
      // IV の時刻を決めたときに、撮影する時刻が空いていることを確かめている
      const fixed = iv + getPetIvLeadMinutes(body1, body2) * 60000
      if (fixed >= earliest.getTime()) schedule = setStartAndEnd(new Date(fixed), modalityTime)
    }
    schedule ||= setOptimizeSchedule(setStartAndEnd(earliest, modalityTime), modalityEvents[modality], modalityTime)
    const minutes = (new Date(schedule.start).getTime() - earliest.getTime()) / 60000
    if (checkWait && !afterIv && !isPetScan(modality, body1, body2) && !waitOver && minutes > MAX_WAIT_MINUTES) waitOver = { exam: `${modality} ${body2}`.trim(), minutes }
    return schedule
  }

  // 必要なモダリティの時間を取得
  const modalityTime = getModalityTime(modality, body1, body2, contrast)

  // モダリティの予約枠を取得
  const modalityEvents = modalityEventsOf(modality)

  // 空き時間を時間を検索
  if (state.prevEnd === undefined) {
    // 受付時刻の20分後（準備時間）に設定（検査開始は8:30から）
    const receptionTime = new Date(reception)
    receptionTime.setMinutes(receptionTime.getMinutes() + RECEPTION_PREP_MINUTES)
    const examStart = atTime(reception, DOCK_EXAM_START)
    if (receptionTime < examStart) receptionTime.setTime(examStart.getTime())
    const origin = new Date(receptionTime)
    if (key["modality"] === 'PET-CT') wait = false
    // ES下部がある場合に ES が先頭なら、受付から前処置（下剤）の時間をあける
    if (key["modality"] === 'ES' && esPrep) {
      receptionTime.setTime(Math.max(receptionTime.getTime(), reception.getTime() + ES_LOWER_PREP_MINUTES * 60000))
      esPrep = false
    }

    // 既存イベントを考慮した設置可能な時刻を検索
    eventSchedule = place(receptionTime, origin)

    // 前回の予約があれば、その次に設定
  } else {
    let prevEnd: Date = new Date(state.prevEnd)
    if (key["modality"] === 'PET-CT') wait = false
    // ES下部がある場合は、最初の ES の前に前処置（下剤）の時間をあける
    if (key["modality"] === 'ES' && esPrep) {
      prevEnd.setMinutes(prevEnd.getMinutes() + ES_LOWER_PREP_MINUTES)
      esPrep = false
    }
    // 前イベントの次に、既存イベントを考慮した設置可能な時刻を検索
    eventSchedule = place(prevEnd, new Date(state.prevEnd))
  }
  // 次の検査の開始時刻として、この検査の終了時刻を控える
  const prevEnd = new Date(eventSchedule.end).getTime()

  // endが終了時刻 DOCK_EXAM_END を過ぎていた場合、17時に仮置きし、手動でスケジュールを決めてもらう。
  // IV の時刻が無かった PET-CT も同じく仮置きする
  const late = state.late || eventSchedule.end > atTime(reception, DOCK_EXAM_END)
  let overtime = state.overtime || ivUnavailable || late || !!waitOver
  if ((ivUnavailable && !state.ivUnavailable) || eventSchedule.end > atTime(reception, DOCK_EXAM_END)) {
    const lastTime = new Date(reception)
    lastTime.setHours(17, 0, 0, 0)
    eventSchedule = setStartAndEnd(lastTime, modalityTime)
    overtime = true
  }

  // 検出したスケジュールの開始時刻、終了時刻をセット
  const duration = {
    ...state.duration,
    [modality + body1 + body2 + contrast]: {
      "start": eventSchedule.start,
      "end": eventSchedule.end,
      "status": 0,
      "no_schedule": nonScheduleModality.includes(modality)
    },
  }
  return { duration, overtime, late, wait, esPrep, iv, ivUnavailable, waitOver, prevEnd }
}
